import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { saveAs } from 'file-saver';
import { Link, useHistory, useLocation } from 'react-router-dom';

import CommonHeader from '../../../util_components/common_header';
import CustomLoading from '../../../util_components/custom_loading';
import NoPermission from '../../../util_components/no_permission';
import LoadFailed from '../shared/load_failed';
import StatusPill from '../shared/status_pill';
import { SelectFilter } from '../shared/controls';
import { error_message, is_permission_error, month_label } from '../format';
import { months_view } from '../overview/adapters';
import { accept_month_choice, pick_initial_month } from '../overview/params';
import { month_from_search, with_month } from '../shared/month_query';
import {
  fetch_mappings,
  fetch_months,
  fetch_statement,
  post_mapping_remove,
  post_mapping_set
} from './api';
import {
  apply_mapping,
  by_teacher,
  by_teacher_csv,
  lines_csv,
  check_sheet_file,
  choice_value,
  compare_sheet,
  differences_csv,
  file_name,
  mapping_error_text,
  mapping_from_api,
  mapping_set_body,
  mapping_targets,
  mappings_view,
  parse_choice,
  read_sheet,
  set_mapping_choice,
  statement_csv,
  statement_view
} from './logic';
import StatementResults from './results';
import '../teacher_payments.css';
import '../page_shell.css';
import './statement.css';

const csv_blob = (text) => new Blob([text], { type: 'text/csv;charset=utf-8' });
const read_text = (file) =>
  typeof file.text === 'function'
    ? file.text()
    : new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result || ''));
        r.onerror = () => reject(r.error);
        r.readAsText(file);
      });

/*
 * Ops statement (/teacher-payments/statement?month=YYYY-MM): download the month in the invoice
 * sheet's columns, then compare an edited copy of that sheet in the browser. Logic: ./logic.js.
 */
const TeacherPaymentsStatement = () => {
  const history = useHistory();
  const location = useLocation();
  const url_month = useRef(month_from_search(location.search));

  const [no_permission, setNoPermission] = useState(false);
  const [months, setMonths] = useState([]);
  const [months_state, setMonthsState] = useState('loading'); // loading | ready | empty | error
  const [months_rev, setMonthsRev] = useState(0);
  const [month, setMonth] = useState('');

  const [statement, setStatement] = useState({ state: 'idle', view: null, error: '' });
  const [stmt_rev, setStmtRev] = useState(0);

  const [sheet, setSheet] = useState({ name: '', parsed: null, error: '', reading: false });
  const file_input = useRef(null);
  // Ops' shared column mappings (goserver). saved=false: older server (404), this session only.
  const [mapping, setMapping] = useState({});
  const [mapping_saved, setMappingSaved] = useState(true);
  const [map_errors, setMapErrors] = useState({}); // match key -> inline error
  const [map_load_failed, setMapLoadFailed] = useState(false);
  const [map_busy, setMapBusy] = useState(''); // match key being saved

  useEffect(() => {
    let cancelled = false;
    fetch_mappings()
      .then((data) => {
        if (!cancelled) setMapping(mappings_view(data).mapping);
      })
      .catch((e) => {
        if (cancelled) return;
        // 404: goserver without the mappings routes; anything else: still usable, just not shared.
        setMappingSaved(false);
        if (!(e && e.response && e.response.status === 404)) {
          console.warn('statement mappings load failed', e && e.message); // eslint-disable-line no-console
          setMapLoadFailed(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setMonthsState('loading');
    fetch_months()
      .then((data) => {
        if (cancelled) return;
        const list = months_view(data).months;
        setMonths(list);
        setMonth((cur) =>
          accept_month_choice(cur, list) ? cur : pick_initial_month(list, url_month.current)
        );
        setMonthsState(list.length ? 'ready' : 'empty');
      })
      .catch((e) => {
        if (cancelled) return;
        if (is_permission_error(e)) setNoPermission(true);
        setMonthsState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [months_rev]);

  // ?month= mirrors the picker (replace: a month change is not a history step).
  useEffect(() => {
    if (!month) return;
    const next = with_month(location.search, month);
    if (location.search.replace(/^\?/, '') !== next)
      history.replace({ pathname: location.pathname, search: `?${next}` });
  }, [month, history, location.pathname, location.search]);

  useEffect(() => {
    if (!month) return undefined;
    let cancelled = false;
    setStatement({ state: 'loading', view: null, error: '' });
    fetch_statement(month)
      .then((data) => {
        if (!cancelled) setStatement({ state: 'ready', view: statement_view(data), error: '' });
      })
      .catch((e) => {
        if (cancelled) return;
        if (is_permission_error(e)) setNoPermission(true);
        setStatement({
          state: 'error',
          view: null,
          error: error_message(e, 'Could not load the statement for this month.')
        });
      });
    return () => {
      cancelled = true;
    };
  }, [month, stmt_rev]);

  // A sheet belongs to one month: a month change drops it.
  useEffect(() => {
    setSheet({ name: '', parsed: null, error: '', reading: false });
  }, [month]);

  const view = statement.view;
  const base = useMemo(
    () => (view && sheet.parsed ? compare_sheet(view, sheet.parsed) : null),
    [view, sheet.parsed]
  );
  const applied = useMemo(
    () => (base ? apply_mapping(view, base, mapping) : null),
    [view, base, mapping]
  );
  // The base compare already is the answer when nothing in this sheet is mapped.
  const result = useMemo(
    () => (applied && applied.mapped.length ? compare_sheet(applied.view, sheet.parsed) : base),
    [applied, base, sheet.parsed]
  );
  const teachers = useMemo(() => (result ? by_teacher(result) : null), [result]);
  const mapping_props = useMemo(
    () =>
      base
        ? {
            columns: base.unrecognised_columns.map((u) => ({
              ...u,
              value: choice_value(mapping[u.match_key])
            })),
            targets: mapping_targets(view),
            mapped: applied.mapped,
            conflicts: applied.conflicts,
            has_saved: Object.keys(mapping).length > 0,
            saved: mapping_saved,
            errors: map_errors,
            busy: map_busy
          }
        : null,
    [base, applied, mapping, view, mapping_saved, map_errors, map_busy]
  );
  const set_error = (k, text) => setMapErrors((m) => ({ ...m, [k]: text }));
  // Saved: set/remove on the server, then rerun with its answer. Session-only: apply locally.
  const on_map = (match_key, value) => {
    const choice = parse_choice(value);
    const column = (base ? base.unrecognised_columns : []).find((u) => u.match_key === match_key);
    if (!column) return;
    set_error(match_key, '');
    if (!mapping_saved) {
      setMapping((m) => set_mapping_choice(m, match_key, choice));
      return;
    }
    setMapBusy(match_key);
    const req = choice
      ? post_mapping_set(mapping_set_body(column.header, choice)).then((d) => {
          const got = mapping_from_api(d && d.mapping) || [match_key, choice];
          setMapping((m) => set_mapping_choice(m, match_key, got[1]));
        })
      : post_mapping_remove({ sheet_header: column.header }).then(() =>
          setMapping((m) => set_mapping_choice(m, match_key, null))
        );
    req.catch((e) => set_error(match_key, mapping_error_text(e))).finally(() => setMapBusy(''));
  };

  const download_statement = () => {
    if (!view || !view.rows.length) return;
    saveAs(csv_blob(statement_csv(view)), file_name('statement', month));
  };
  const download_differences = useCallback(
    (cells) => {
      if (!cells.length) return;
      saveAs(csv_blob(differences_csv(cells)), file_name('statement-differences', month));
    },
    [month]
  );
  const download_teachers = useCallback(
    (rows) => {
      if (!rows.length) return;
      saveAs(csv_blob(by_teacher_csv(rows)), file_name('statement-by-teacher', month));
    },
    [month]
  );
  const download_lines = useCallback(
    (lines, t, cell) => {
      if (!lines.length) return;
      saveAs(
        csv_blob(lines_csv(lines, t, cell)),
        file_name(`statement-lines-${t.teacher_id}-${cell.key}`, month)
      );
    },
    [month]
  );

  const on_file = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ''; // the same file picked again re-reads it
    if (!file) return;
    const problem = check_sheet_file(file);
    if (problem) {
      setSheet({ name: file.name, parsed: null, error: problem, reading: false });
      return;
    }
    setSheet({ name: file.name, parsed: null, error: '', reading: true });
    read_text(file)
      .then((text) => {
        const parsed = read_sheet(text);
        setSheet({
          name: file.name,
          parsed: parsed.error ? null : parsed,
          error: parsed.error || '',
          reading: false
        });
      })
      .catch(() =>
        setSheet({
          name: file.name,
          parsed: null,
          error: 'Could not read the file.',
          reading: false
        })
      );
  };
  const clear_sheet = () => setSheet({ name: '', parsed: null, error: '', reading: false });

  if (no_permission) return <NoPermission />;

  const label = month ? month_label(month) : '';
  const ready = statement.state === 'ready' && view;
  const has_rows = ready && view.rows.length > 0;
  const back_search = month ? `?${with_month('', month)}` : '';

  return (
    <CommonHeader loading={false} title="Statement and compare" class_name="tp-fullwidth">
      <div className="tp-page tp-st">
        <Link className="tp-link tp-st-back" to={`/teacher-payments/overview${back_search}`}>
          ← Back to Overview
        </Link>
        <div className="tp-header">
          <span className="tp-title">Statement and compare</span>
          {months_state === 'ready' ? (
            <div className="tp-hd-month">
              <SelectFilter
                id="tp-st-month"
                label="Month"
                aria_label="Payment month"
                value={month}
                options={months.map((m) => ({ value: m.pay_month, label: m.label }))}
                active={false}
                onChange={(v) => accept_month_choice(v, months) && setMonth(v)}
              />
            </div>
          ) : null}
        </div>

        {months_state === 'loading' ? <CustomLoading /> : null}
        {months_state === 'error' ? (
          <LoadFailed
            text="Could not load the payment months."
            on_retry={() => setMonthsRev((r) => r + 1)}
          />
        ) : null}
        {months_state === 'empty' ? (
          <div className="tp-empty">No payment month has been computed yet.</div>
        ) : null}

        {month ? (
          <>
            <div className="tp-card">
              <div className="tp-card-hd">
                <span className="tp-card-hd-main">
                  <span className="tp-card-hd-title">1. Download statement · {label}</span>
                  <span className="tp-card-hd-sub">
                    {has_rows
                      ? `${view.rows.length} teachers · ${view.columns.length} columns in the invoice-sheet order`
                      : 'The invoice-sheet columns, one row per teacher'}
                  </span>
                </span>
                <span className="tp-card-hd-acts">
                  <button
                    type="button"
                    className="tp-btn"
                    onClick={download_statement}
                    aria-disabled={has_rows ? undefined : 'true'}
                  >
                    ↓ Download statement CSV
                  </button>
                </span>
              </div>
              <div className="tp-st-body">
                {statement.state === 'loading' ? <CustomLoading /> : null}
                {statement.state === 'error' ? (
                  <LoadFailed text={statement.error} on_retry={() => setStmtRev((r) => r + 1)} />
                ) : null}
                {ready && !has_rows ? (
                  <div className="tp-empty">No teachers have a statement for {label}.</div>
                ) : null}
                {has_rows && view.rows.some((r) => !r.ties_out) ? (
                  <p className="tp-st-note tp-st-ties">
                    <StatusPill tone="orange">
                      {view.rows.filter((r) => !r.ties_out).length} to check
                    </StatusPill>{' '}
                    Their lines do not add up to Grand Total yet (waiting for a recompute); check
                    them before paying. They are flagged per teacher after a compare.
                  </p>
                ) : null}
                {has_rows ? (
                  <p className="tp-st-note">
                    Rows are ordered by teacher name. Amounts are plain numbers; a blank cell has no
                    value.
                    {view.unmapped.length
                      ? ` ${view.unmapped.length} column${view.unmapped.length === 1 ? ' is' : 's are'} not filled yet (listed after a compare).`
                      : ''}
                  </p>
                ) : null}
              </div>
            </div>

            <div className="tp-card">
              <div className="tp-card-hd">
                <span className="tp-card-hd-main">
                  <span className="tp-card-hd-title">2. Compare with your sheet</span>
                  <span className="tp-card-hd-sub">
                    CSV up to 2 MB, same columns as the download
                  </span>
                </span>
                <span className="tp-card-hd-acts">
                  <input
                    ref={file_input}
                    id="tp-st-file"
                    className="sr-only"
                    type="file"
                    accept=".csv,text/csv"
                    onChange={on_file}
                    disabled={!has_rows}
                  />
                  <button
                    type="button"
                    className="tp-btn"
                    onClick={() => has_rows && file_input.current && file_input.current.click()}
                    aria-disabled={has_rows && !sheet.reading ? undefined : 'true'}
                  >
                    {sheet.reading ? 'Reading…' : '↑ Upload your sheet'}
                  </button>
                </span>
              </div>
              <div className="tp-st-body">
                <p className="tp-st-note">
                  Your file is read in this browser only. Nothing is uploaded or saved. Rows match
                  by teacher_id; every money and day column is compared. Money differences up to ₹1
                  are ignored; day differences always count and are never added to the ₹ totals.
                </p>
                {map_load_failed ? (
                  <div className="tp-banner tp-banner-error" role="alert">
                    Could not load your saved column mappings. Mappings you set now last only for
                    this session.
                  </div>
                ) : null}
                {sheet.error ? (
                  <div className="tp-banner tp-banner-error" role="alert">
                    {sheet.name ? `${sheet.name}: ` : ''}
                    {sheet.error}
                  </div>
                ) : null}
                {!result && !sheet.error && has_rows ? (
                  <div className="tp-empty">Upload your sheet to see the differences.</div>
                ) : null}
              </div>
            </div>

            {result ? (
              <StatementResults
                key={`${month}|${sheet.name}`}
                result={result}
                teachers={teachers}
                mapping={mapping_props}
                file_name={sheet.name}
                on_download={download_differences}
                on_download_teachers={download_teachers}
                on_download_lines={download_lines}
                month={month}
                on_map={on_map}
                on_clear={clear_sheet}
              />
            ) : null}
          </>
        ) : null}
      </div>
    </CommonHeader>
  );
};

export default TeacherPaymentsStatement;
